import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ChevronRight, FileText, Folder, HardDrive, Laptop } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastError } from "@/lib/toast";
import { TEAM_FILE_ACCEPT, TEAM_FILE_TYPES_HINT } from "@/lib/teamFileUpload";
import { cn } from "@/lib/utils";

/**
 * Attach sources: this device, Team Files, Drive stub.
 */
export default function DmAttachPickerDialog({
  open,
  onOpenChange,
  accessToken,
  orgId,
  channelTeamId,
  remaining = 10,
  onPickLocalFiles,
  onPickTeamFiles,
}) {
  const [source, setSource] = useState("device");
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState(channelTeamId || null);
  const [parentId, setParentId] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Map());

  const lockedTeam = channelTeamId != null && Number.isFinite(Number(channelTeamId));

  useEffect(() => {
    if (!open) return;
    setSource("device");
    setSelected(new Map());
    setParentId(null);
    if (lockedTeam) {
      setTeamId(Number(channelTeamId));
    }
  }, [open, channelTeamId, lockedTeam]);

  useEffect(() => {
    if (!open || !accessToken || !orgId || lockedTeam) return undefined;
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/team/memberships`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { org_id: orgId },
      })
      .then((res) => {
        if (cancelled) return;
        const list = (Array.isArray(res.data) ? res.data : [])
          .map((t) => ({
            team_id: Number(t.team_id ?? t.teamId),
            name: t.name || `Team ${t.team_id}`,
          }))
          .filter((t) => Number.isFinite(t.team_id));
        setTeams(list);
        setTeamId((prev) => prev || list[0]?.team_id || null);
      })
      .catch((err) => toastError(err, "Could not load teams"));
    return () => {
      cancelled = true;
    };
  }, [open, accessToken, orgId, lockedTeam]);

  const loadFolder = useCallback(async () => {
    if (!open || !accessToken || !orgId || !teamId || source !== "team") return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/team/${teamId}/files`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: {
          org_id: orgId,
          parent_id: parentId == null ? "null" : parentId,
        },
      });
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      setBreadcrumbs(
        Array.isArray(res.data?.breadcrumbs) ? res.data.breadcrumbs : []
      );
    } catch (err) {
      toastError(err, "Could not load Team Files");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [open, accessToken, orgId, teamId, parentId, source]);

  useEffect(() => {
    loadFolder();
  }, [loadFolder]);

  const toggleFile = (item) => {
    if (item.kind !== "file") return;
    setSelected((prev) => {
      const next = new Map(prev);
      const key = `${teamId}:${item.node_id}`;
      if (next.has(key)) next.delete(key);
      else {
        if (next.size >= remaining) {
          toastError(`You can attach up to ${remaining} more file(s)`);
          return prev;
        }
        next.set(key, {
          team_id: Number(teamId),
          node_id: item.node_id,
          name: item.name,
          mime_type: item.mime_type,
          size_bytes: item.size_bytes,
          previewable: item.previewable,
          visibility: item.visibility,
        });
      }
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Attach files</DialogTitle>
        </DialogHeader>
        <Tabs value={source} onValueChange={setSource} className="flex-1 min-h-0 flex flex-col">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="device" className="text-xs sm:text-sm">
              <Laptop className="h-3.5 w-3.5 mr-1" />
              This device
            </TabsTrigger>
            <TabsTrigger value="team" className="text-xs sm:text-sm">
              <Folder className="h-3.5 w-3.5 mr-1" />
              Team Files
            </TabsTrigger>
            <TabsTrigger value="drive" disabled className="text-xs sm:text-sm" title="Coming soon">
              <HardDrive className="h-3.5 w-3.5 mr-1" />
              Drive
            </TabsTrigger>
          </TabsList>
          <TabsContent value="device" className="pt-3 space-y-2">
            <p className="text-sm text-muted-foreground">
              Choose files from your computer ({TEAM_FILE_TYPES_HINT}). Up to{" "}
              {remaining} more.
            </p>
            <input
              type="file"
              multiple
              accept={TEAM_FILE_ACCEPT}
              className="block w-full text-sm"
              onChange={(e) => {
                const list = Array.from(e.target.files || []);
                e.target.value = "";
                if (list.length) {
                  onPickLocalFiles(list);
                  onOpenChange(false);
                }
              }}
            />
          </TabsContent>
          <TabsContent value="team" className="pt-3 flex-1 min-h-0 flex flex-col gap-2">
            {!lockedTeam && (
              <select
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                value={teamId || ""}
                onChange={(e) => {
                  setTeamId(Number(e.target.value) || null);
                  setParentId(null);
                  setSelected(new Map());
                }}
              >
                {teams.map((t) => (
                  <option key={t.team_id} value={t.team_id}>
                    {t.name}
                  </option>
                ))}
              </select>
            )}
            <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
              <button
                type="button"
                className="hover:text-foreground"
                onClick={() => setParentId(null)}
              >
                Root
              </button>
              {breadcrumbs.map((crumb) => (
                <span key={crumb.node_id} className="inline-flex items-center gap-1">
                  <ChevronRight className="h-3 w-3" />
                  <button
                    type="button"
                    className="hover:text-foreground"
                    onClick={() => setParentId(crumb.node_id)}
                  >
                    {crumb.name}
                  </button>
                </span>
              ))}
            </div>
            <div className="flex-1 min-h-[12rem] overflow-y-auto rounded-md border border-border">
              {loading ? (
                <p className="p-3 text-sm text-muted-foreground">Loading…</p>
              ) : items.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">Empty folder</p>
              ) : (
                <ul>
                  {items.map((item) => {
                    const key = `${teamId}:${item.node_id}`;
                    const checked = selected.has(key);
                    const isFolder = item.kind === "folder";
                    return (
                      <li key={item.node_id}>
                        <button
                          type="button"
                          className={cn(
                            "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/60",
                            checked && "bg-muted"
                          )}
                          onClick={() => {
                            if (isFolder) {
                              setParentId(item.node_id);
                              return;
                            }
                            toggleFile(item);
                          }}
                        >
                          {isFolder ? (
                            <Folder className="h-4 w-4 text-primary shrink-0" />
                          ) : (
                            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                          )}
                          <span className="truncate flex-1">{item.name}</span>
                          {!isFolder && checked ? (
                            <span className="text-xs text-primary">Added</span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={selected.size === 0}
                onClick={() => {
                  onPickTeamFiles([...selected.values()]);
                  onOpenChange(false);
                }}
              >
                Attach {selected.size || ""}
              </Button>
            </div>
          </TabsContent>
          <TabsContent value="drive" className="pt-3">
            <p className="text-sm text-muted-foreground">Coming soon.</p>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
